import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import {
  ApiData,
  ApiMessage,
  ApiResponse,
} from '../../interfaces/api/api-response.interface';
import {
  PlayerPromotionalCodeActors,
  PromotionalCode,
} from '../../interfaces/api/promotional-tools.interface';
import { Observable, tap } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { ToastrNotificationService } from '../ui/toastr-notification.service';
import { EncryptionService } from '../ui/encryption.service';

@Injectable({
  providedIn: 'root',
})
export class PromotionalCodeService {
  private apiUrl: string = environment.apiUrl;
  private path: string = '/promotional-code';
  private path_player: string = '/player-promotional-code';
  public url: string = `${this.apiUrl}${this.path}`;
  public url_player: string = `${this.apiUrl}${this.path_player}`;

  constructor(
    private _httpClient: HttpClient,
    private _notificationService: ToastrNotificationService,
    private _encryptionService: EncryptionService
  ) {}

  getPromotionalCodes(
    filter: object
  ): Observable<ApiResponse<ApiData<PromotionalCode[]>>> {
    const params = new HttpParams({ fromObject: { ...filter } });
    const endpoint = `${this.url}/get-all`;
    return this._httpClient.get<ApiResponse<ApiData<PromotionalCode[]>>>(
      endpoint,
      {
        params,
      }
    );
  }

  getPlayerPromotionalCodeActors(
    playerPromotionalCodeId: string
  ): Observable<ApiResponse<PlayerPromotionalCodeActors>> {
    const params = new HttpParams({ fromObject: { playerPromotionalCodeId } });
    return this._httpClient.get<
      ApiResponse<PlayerPromotionalCodeActors>
    >(`${this.url_player}/actors`, { params });
  }

  getPlayerPromotionalCodes(
    filter: any
  ): Observable<ApiResponse<ApiData<PromotionalCode[]>>> {
    const data = this._encryptionService.encryptWithDate(
      JSON.stringify({
        playerId: filter.playerId,
        page: filter.page,
        limit: filter.limit,
      })
    );
    const endpoint = `${this.url_player}/get-all-promotional-code-by-player`;
    return this._httpClient.post<ApiResponse<ApiData<PromotionalCode[]>>>(
      endpoint,
      {
        data,
      }
    );
  }

  public activePromotionalCodeByPlayer(
    playerPromotionalCodeId: string
  ): Observable<ApiResponse<Object>> {
    const data = this._encryptionService.encrypt(
      JSON.stringify({ playerPromotionalCodeId })
    );
    return this._httpClient.post<ApiResponse<Object>>(
      `${this.url_player}/activate`,
      { data }
    );
  }

  savePromotionalCode(request: object): Observable<ApiResponse<{}>> {
    const $URL = `${this.url}/save-promotional-code`;
    return this._httpClient
      .post<ApiResponse<{}>>($URL, request)
      .pipe(tap((res) => this.showNotification(res.message)));
  }

  updatePromotionalCode(
    id: string,
    request: object
  ): Observable<ApiResponse<{}>> {
    const $URL = `${this.url}/update/${id}`;
    return this._httpClient
      .put<ApiResponse<{}>>($URL, request)
      .pipe(tap((res) => this.showNotification(res.message)));
  }

  private showNotification(message: ApiMessage): void {
    this._notificationService.showNotification({
      title: 'promotionalTools.promotionalCodes.title',
      message: message,
      type: 'success',
    });
  }

  public cancelPromotionalCodeByPlayer(
    playerPromotionalCodeId: string,
    reason: string = 'Canceled by admin'
  ): Observable<ApiResponse<Object>> {
    const $URL = `${this.url_player}/cancel`;
    const data = this._encryptionService.encrypt(
      JSON.stringify({ playerPromotionalCodeId, reason })
    );
    return this._httpClient.post<ApiResponse<Object>>($URL, { data });
  }

  public setDepositPromotionalCodeByPlayer(
    playerId: string,
    amountDeposited: number
  ): Observable<ApiResponse<Object>> {
    const $URL = `${this.url_player}/set-deposit`;
    return this._httpClient.post<ApiResponse<Object>>($URL, {
      playerId,
      amountDeposited,
      isBackOffice: true,
    });
  }

  public setAmountBettedPromotionalCodeByPlayer(
    playerPromotionalCodeId: string,
    amountBetted: number
  ): Observable<ApiResponse<Object>> {
    const $URL = `${this.url_player}/update-amount-betted`;
    const data = this._encryptionService.encrypt(
      JSON.stringify({ playerPromotionalCodeId, amountBetted })
    );
    return this._httpClient.patch<ApiResponse<Object>>($URL, { data });
  }
}
